import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Search,
  PlusCircle,
  Heart,
  User,
  ShoppingCart,
  Zap,
  Camera,
  Compass,
  FileText, ChevronLeft 
} from 'lucide-react';
import { useState, useEffect  } from 'react';
import { Link, useLocation } from 'react-router';

interface NavItem {
  icon: React.ElementType;
  label: string;
  href: string;
  badge?: number;
}

const navItems: NavItem[] = [
  { icon: Home, label: 'Home', href: '/dashboard' },
  { icon: Search, label: 'Explore', href: '/feed' },
  { icon: PlusCircle, label: 'Create', href: '/projects/new' },
  { icon: ShoppingCart, label: 'Market', href: '/marketplace' },
  { icon: User, label: 'Profile', href: '/settings' },
];

interface MobileNavigationProps {
  className?: string;
}

export function MobileNavigation({ className }: MobileNavigationProps) {
  const location = useLocation();
  const [showQuickActions, setShowQuickActions] = useState(false);

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const quickActions = [
    { icon: Camera, label: 'Photo', action: () => {} },
    { icon: FileText, label: 'Note', action: () => {} },
    { icon: Compass, label: 'Stage', action: () => {} },
    { icon: Zap, label: 'Energy', action: () => {} },
  ];

  return (
    <>
      {/* Quick Actions Menu */}
      <AnimatePresence>
        {showQuickActions && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowQuickActions(false)}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 100 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 100 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 md:hidden"
            >
              <div className="bg-background rounded-2xl shadow-xl p-4 grid grid-cols-4 gap-4">
                {quickActions.map((action) => (
                  <motion.button
                    key={action.label}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      action.action();
                      setShowQuickActions(false);
                    }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-secondary transition-colors"
                  >
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <action.icon className="h-6 w-6 text-primary" />
                    </div>
                    <span className="text-xs font-medium">{action.label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom Navigation Bar */}
      <motion.nav
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={cn(
          "fixed bottom-0 left-0 right-0 z-30",
          "bg-background/95 backdrop-blur-lg border-t",
          "px-4 pb-safe md:hidden",
          className
        )}
      >
        <div className="flex items-center justify-around h-16">
          {navItems.map((item, index) => {
            const isCenter = index === 2;
            const active = isActive(item.href);

            if (isCenter) {
              return (
                <motion.button
                  key={item.label}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowQuickActions(!showQuickActions)}
                  className="relative"
                >
                  <motion.div
                    animate={showQuickActions ? { rotate: 45 } : { rotate: 0 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="w-14 h-14 -mt-4 rounded-full bg-primary flex items-center justify-center shadow-lg"
                  >
                    <item.icon className="h-7 w-7 text-primary-foreground" />
                  </motion.div>
                </motion.button>
              );
            }

            return (
              <Link
                key={item.label}
                to={item.href}
                className="relative flex flex-col items-center gap-1 py-2 px-3 min-w-[64px]"
              >
                <motion.div
                  initial={false}
                  animate={active ? { scale: 1.1 } : { scale: 1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <item.icon
                    className={cn(
                      "h-6 w-6 transition-colors",
                      active ? "text-primary" : "text-muted-foreground"
                    )}
                  />
                </motion.div>
                <span
                  className={cn(
                    "text-xs font-medium transition-colors",
                    active ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  {item.label}
                </span>
                {active && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary"
                  />
                )}
                {item.badge && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </motion.nav>
    </>
  );
}

// Mobile Header with gesture support
interface MobileHeaderProps {
  title: string;
  onBack?: () => void;
  actions?: React.ReactNode;
  className?: string;
}

export function MobileHeader({ title, onBack, actions, className }: MobileHeaderProps) {
  const [scrollY, setScrollY] = useState(0);

  // Handle scroll for dynamic header
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const headerOpacity = Math.min(scrollY / 100, 1);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      style={{
        backgroundColor: `rgba(255, 255, 255, ${0.7 + headerOpacity * 0.3})`,
        backdropFilter: `blur(${8 + headerOpacity * 4}px)`,
      }}
      className={cn(
        "fixed top-0 left-0 right-0 z-30",
        "border-b transition-all duration-200",
        "px-4 pt-safe",
        className
      )}
    >
      <div className="flex items-center justify-between h-14">
        {onBack ? (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="p-2 -ml-2 rounded-full hover:bg-secondary transition-colors"
          >
            <ChevronLeft className="h-6 w-6" />
          </motion.button>
        ) : (
          <div className="w-10" />
        )}
        
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-lg font-semibold"
        >
          {title}
        </motion.h1>
        
        <div className="flex items-center gap-2">
          {actions}
        </div>
      </div>
    </motion.header>
  );
}

import { cn } from '#app/utils/misc.tsx';
